import React from 'react';
import { UI_Z_CLASS } from '../../utils/uiZIndex';
import { SETTINGS_BTN_OUTLINE, SETTINGS_BTN_PRIMARY } from '../settings/utils/settingsSectionStyles';
import { AiShareFields, useAiShareMembers, type AiVisibility } from './AiShareFields';

export function ChatShareDialog({
  chatId,
  initialVisibility,
  initialGrantees,
  onClose,
  onSaved,
}: {
  chatId: string;
  initialVisibility: AiVisibility;
  initialGrantees: string[];
  onClose: () => void;
  onSaved: (visibility: AiVisibility, granteeUserIds: string[]) => void;
}) {
  const [visibility, setVisibility] = React.useState<AiVisibility>(initialVisibility);
  const [granteeUserIds, setGranteeUserIds] = React.useState<string[]>(initialGrantees);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const members = useAiShareMembers();

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/ai/chats/${encodeURIComponent(chatId)}/share`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visibility,
          granteeUserIds: visibility === 'members' ? granteeUserIds : [],
        }),
      });
      const body = await res.json().catch(() => ({})) as { error?: string };
      if (!res.ok) throw new Error(body.error || 'Freigabe konnte nicht gespeichert werden.');
      onSaved(visibility, visibility === 'members' ? granteeUserIds : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Freigabe konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`fixed inset-0 ${UI_Z_CLASS.fullscreenModal} flex items-center justify-center bg-black/40 p-4`}>
      <div className="w-full max-w-md rounded-lg border border-app-border bg-app-surface p-5" role="dialog" aria-labelledby="chat-share-title">
        <h2 id="chat-share-title" className="text-base font-bold text-app-text">Chat teilen</h2>
        <p className="mt-1 text-sm text-app-text-muted">Empfänger können den Verlauf lesen. Schreiben und Anhänge bleiben bei dir.</p>
        <div className="mt-4">
          <AiShareFields
            idPrefix="chat-share"
            visibility={visibility}
            onVisibilityChange={setVisibility}
            granteeUserIds={granteeUserIds}
            onGranteesChange={setGranteeUserIds}
            members={members}
          />
        </div>
        {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={onClose}>Abbrechen</button>
          <button type="button" className={SETTINGS_BTN_PRIMARY} disabled={saving} onClick={() => void save()}>
            {saving ? 'Speichert …' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
